import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Box,
  Paper,
  Divider,
  Stack,
  alpha,
  useTheme,
  CircularProgress,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Print as PrintIcon,
  LocalShipping,
  LocationOn,
  Description,
  Speed,
  Compress,
  Anchor,
} from "@mui/icons-material";
import { useVoyageContextV2 } from "../../Contexts/VoyageContextV2";
import { useCompanyContext } from "../../Contexts/CompanyContext";
import { useTranslation } from "react-i18next";
import { renderPumpStatementV2 } from "../../print-v2";
import { getSelectedCargo, getSelectedTrip } from "../../utils/voyageSelection";
import { getVoyageNumber } from "../../utils/domainSelectors";

const PumpStatement = () => {
  const { t } = useTranslation("pumpstatement");
  const theme = useTheme();

  // V2 Context
  const { selectedTripId, selectedCargoIndex, currentVoyage } =
    useVoyageContextV2();
  const { companyLogo } = useCompanyContext();

  const [maxPressure, setMaxPressure] = useState("");
  const [maxRate, setMaxRate] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "warning",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentVoyage) setLoading(false);
  }, [currentVoyage]);

  const handlePrintButton = () => {
    const selectedCargo = getSelectedCargo(
      currentVoyage,
      selectedTripId,
      selectedCargoIndex
    );

    if (!selectedCargo) {
      setSnackbar({
        open: true,
        message: "Please select a cargo before printing.",
        severity: "warning",
      });
      return;
    }

    renderPumpStatementV2({
      currentVoyage,
      selectedTripId,
      selectedCargoIndex,
      maxPressure,
      maxRate,
      companyLogo,
      t,
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 10,
          gap: 2,
        }}
      >
        <CircularProgress size={60} thickness={4.5} />
        <Typography variant="h6" color="textSecondary">
          Loading Voyage Data...
        </Typography>
      </Box>
    );
  }

  const trip = getSelectedTrip(currentVoyage, selectedTripId);

  // Återanvänder InfoRow-konceptet för en enhetlig look
  const InfoRow = ({ icon, label, value }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5 }}>
      <Box
        sx={{
          color: "primary.main",
          display: "flex",
          p: 1,
          borderRadius: 1.5,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
        }}
      >
        {React.cloneElement(icon, { fontSize: "small" })}
      </Box>
      <Box>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            textTransform: "uppercase",
            fontWeight: 700,
            display: "block",
            mb: -0.5,
          }}
        >
          {label}
        </Typography>
        <Typography variant="body1" fontWeight="600">
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", pb: 5 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="900" gutterBottom>
          {t("title")}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Enter pumping limits and generate the official Pump Statement.
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          position: "relative",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 4 }}>
          <Anchor color="primary" />
          <Typography variant="h6" fontWeight="800">
            {t("voyageInfo.title")}
          </Typography>
        </Stack>

        <Grid container spacing={0}>
          {/* Vänster kolumn: Basinfo */}
          <Grid sx={{ borderRight: { md: `1px solid ${theme.palette.divider}` }, pr: { md: 4 }, }} size={{ xs: 12, md: 6 }}>
            <Stack divider={<Divider flexItem sx={{ opacity: 0.5 }} />}>
              <InfoRow
                icon={<LocalShipping />}
                label={t("voyageInfo.receivers")}
                value={trip?.supplierReceiver}
              />
              <InfoRow
                icon={<LocationOn />}
                label={t("voyageInfo.port")}
                value={trip?.to}
              />
              <InfoRow
                icon={<Description />}
                label={t("voyageInfo.voyage")}
                value={getVoyageNumber(currentVoyage)}
              />
            </Stack>
          </Grid>

          {/* Höger kolumn: Input för Pressure & Rate */}
          <Grid sx={{ pl: { md: 4 }, mt: { xs: 4, md: 0 } }} size={{ xs: 12, md: 6 }}>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{
                textTransform: "uppercase",
                fontWeight: 700,
                mb: 2,
                display: "block",
              }}
            >
              Pumping Restrictions
            </Typography>
            <Stack spacing={3}>
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Compress fontSize="small" color="primary" />
                  <Typography variant="body2" fontWeight="700">
                    {t("voyageInfo.maxPressure")}
                  </Typography>
                </Stack>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="e.g. 7.5"
                  variant="outlined"
                  size="small"
                  value={maxPressure}
                  onChange={(e) => setMaxPressure(e.target.value)}
                  InputProps={{
                    sx: {
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    },
                    endAdornment: (
                      <Typography variant="caption" color="textSecondary">
                        bar
                      </Typography>
                    ),
                  }}
                />
              </Box>

              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Speed fontSize="small" color="primary" />
                  <Typography variant="body2" fontWeight="700">
                    {t("voyageInfo.maxRate")}
                  </Typography>
                </Stack>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="e.g. 1500"
                  variant="outlined"
                  size="small"
                  value={maxRate}
                  onChange={(e) => setMaxRate(e.target.value)}
                  InputProps={{
                    sx: {
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    },
                    endAdornment: (
                      <Typography variant="caption" color="textSecondary">
                        m³/h
                      </Typography>
                    ),
                  }}
                />
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Button */}
      <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<PrintIcon />}
          onClick={handlePrintButton}
          sx={{
            py: 1.8,
            px: 6,
            borderRadius: 3,
            fontWeight: 800,
            fontSize: "1rem",
            boxShadow: theme.shadows[4],
            "&:hover": { boxShadow: theme.shadows[8] },
          }}
        >
          {t("printButton")}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PumpStatement;
